import React, { useState, useEffect } from "react";
import { Auth } from "../../Common/lib/auth";
import { API } from "../../Common/lib/api";
import { getProject } from "../../graphql/queries.js";
import XMLViewer from "react-xml-viewer";
import Header from "../../Common/header";
import Footer from "../../Common/footer";

const Turtle = () => {
  const [projectData, setProjectData] = useState(null);
  const [view, setView] = useState("select");
  const [inputPK, setInputPK] = useState("");
  const [instance, setInstance] = useState(null);

  useEffect(() => {
    if (inputPK) {
      fetchData(inputPK);
    }
  }, [inputPK]);

  const fetchData = async (pk) => {
    try {
      const authMode = await getAuthMode();
      const projectQuery = await API.graphql({
        query: getProject,
        variables: { PK: pk, SK: pk },
        authMode: authMode,
      });
      setProjectData(projectQuery.data.getProject);
    } catch (error) {
      console.error("Error fetching project:", error);
    }
  };

  async function getAuthMode() {
    try {
      await Auth.currentAuthenticatedUser();
      return "AMAZON_COGNITO_USER_POOLS";
    } catch {
      return "AWS_IAM";
    }
  }

  const formatAsTurtleIdentificationInfo = (data) => {
    if (!data) return "";

    const {
      PK,
      citation,
      abstract,
      purpose,
      stakeholders,
      publishStatus,
    } = data;

    const rdfNamespace = "http://www.w3.org/1999/02/22-rdf-syntax-ns#";
    const rdfsNamespace = "http://www.w3.org/2000/01/rdf-schema#";
    const xsdNamespace = "http://www.w3.org/2001/XMLSchema#";
    const arfNamespace =
      "http://arcrcticesearch-20220915111649-hostingbucket-admintest.s3-website-us-east-1.amazonaws.com/Ontology#";

    return `
      @prefix rdf: <${rdfNamespace}> .
      @prefix rdfs: <${rdfsNamespace}> .
      @prefix xsd: <${xsdNamespace}> .
      @prefix arf: <${arfNamespace}> .

      # Identification Information Turtle Data
      arf:IdentificationInformation a rdfs:Class ;
        rdfs:label "Identification Information" .

      arf:citation a rdf:Property ; rdfs:label "citation" .
      arf:abstract a rdf:Property ; rdfs:label "abstract" .
      arf:purpose a rdf:Property ; rdfs:label "purpose" .
      arf:stakeholders a rdf:Property ; rdfs:label "stakeholders" .
      arf:publishStatus a rdf:Property ; rdfs:label "publishStatus" .

      arf:identificationInfo a arf:IdentificationInformation ;
        arf:citation "${citation}" ;
        arf:abstract "${abstract}" ;
        arf:purpose "${purpose}" ;
        arf:stakeholders "${stakeholders}" ;
        arf:publishStatus "${publishStatus}" .
    `;
  };

  const formatAsTurtleMetadataSet = (data) => {
    if (!data) return "";

    const {
      PK,
      createdAt,
      DOI,
      title,
      keywords,
    } = data;

    const rdfNamespace = "http://www.w3.org/1999/02/22-rdf-syntax-ns#";
    const rdfsNamespace = "http://www.w3.org/2000/01/rdf-schema#";
    const xsdNamespace = "http://www.w3.org/2001/XMLSchema#";
    const arfNamespace =
      "http://arcrcticesearch-20220915111649-hostingbucket-admintest.s3-website-us-east-1.amazonaws.com/Ontology#";

    return `
      @prefix rdf: <${rdfNamespace}> .
      @prefix rdfs: <${rdfsNamespace}> .
      @prefix xsd: <${xsdNamespace}> .
      @prefix arf: <${arfNamespace}> .

      # Metadata Entity Set Information Turtle Data
      arf:MetadataEntitySetInformation a rdfs:Class ;
        rdfs:label "Metadata Entity Set Information" .

      arf:PK a rdf:Property ; rdfs:label "PK" .
      arf:createdAt a rdf:Property ; rdfs:label "createdAt" .
      arf:DOI a rdf:Property ; rdfs:label "DOI" .
      arf:title a rdf:Property ; rdfs:label "title" .
      arf:keywords a rdf:Property ; rdfs:label "keywords" .

      arf:metadataSet a arf:MetadataEntitySetInformation ;
        arf:PK "${PK}" ;
        arf:createdAt "${createdAt}" ;
        arf:DOI "${DOI}" ;
        arf:title "${title}" ;
        arf:keywords "${keywords}" .
    `;
  };

  const formatAsXML = (data) => {
    if (!data) return "";
    return `
      <metadata>
        <fileIdentifier>${data.PK}</fileIdentifier>
        <createdAt>${data.createdAt}</createdAt>
        <doi>${data.DOI}</doi>
        <abstract>${data.abstract}</abstract>
        <title>${data.title}</title>
      </metadata>
    `;
  };

  const buttonStyle = {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "10px 20px",
    cursor: "pointer",
    marginRight: "10px",
    marginTop: "10px",
  };

  return (
    <>
      <Header />
      <div>
        <div>
          <input
            type="text"
            value={inputPK}
            onChange={(e) => setInputPK(e.target.value)}
            placeholder="Enter PK"
          />
        </div>

        {view === "select" && (
          <div>
            <h1>Select Data Format</h1>
            <button
              style={buttonStyle}
              onClick={() => setView("turtle")}
            >
              View as Turtle
            </button>
            <button
              style={buttonStyle}
              onClick={() => setView("xml")}
            >
              View as XML
            </button>
          </div>
        )}

        {view === "turtle" && (
          <div>
            <h1>Select Turtle Instance</h1>
            <button
              style={buttonStyle}
              onClick={() => setInstance("identificationInfo")}
            >
              Identification Information
            </button>
            <button
              style={buttonStyle}
              onClick={() => setInstance("metadataSet")}
            >
              Metadata Entity Set Information
            </button>
          </div>
        )}

        {instance === "identificationInfo" && view === "turtle" && projectData && (
          <div>
            <h1>Identification Information Turtle Data</h1>
            <pre>{formatAsTurtleIdentificationInfo(projectData)}</pre>
            <button
              style={buttonStyle}
              onClick={() => setInstance(null)}
            >
              Back to Selection
            </button>
            <button
              style={buttonStyle}
              onClick={() => setView("select")}
            >
              Back to Format Selection
            </button>
          </div>
        )}

        {instance === "metadataSet" && view === "turtle" && projectData && (
          <div>
            <h1>Metadata Entity Set Information Turtle Data</h1>
            <pre>{formatAsTurtleMetadataSet(projectData)}</pre>
            <button
              style={buttonStyle}
              onClick={() => setInstance(null)}
            >
              Back to Selection
            </button>
            <button
              style={buttonStyle}
              onClick={() => setView("select")}
            >
              Back to Format Selection
            </button>
          </div>
        )}

        {view === "xml" && projectData && (
          <div>
            <h1>XML Data</h1>
            <XMLViewer xml={formatAsXML(projectData)} />
            <button
              style={buttonStyle}
              onClick={() => setView("select")}
            >
              Back
            </button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default Turtle;
